"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.transformForClass = exports.remove = exports.augment = exports.wrap = exports.replace = exports.byId = void 0;
const byClass = (cls) => (region) => region.classes.includes(cls);
/**
 * Build a matcher to select one region by id.
 *
 * @param id The full region path to match.
 * @returns A matcher that returns true only for the exact path.
 */
const byId = (id) => (region) => region.id === id;
exports.byId = byId;
/**
 * Replaces the region's content with the rendered output. Last-registered
 * wins if multiple plugins replace the same region.
 *
 * @param cls Region class to target.
 * @param render Receives the typed ctx. Returns the replacement node.
 * @param opts Optional id and dependsOn.
 * @returns A `ReplaceOperation` to include in `ClientPlugin.ui`.
 *
 * @example
 * ```ts
 * replace("phase-timer", ({ phaseEndTimeSeconds }) => <TimerBar end={phaseEndTimeSeconds} />)
 * ```
 */
const replace = (cls, render, opts) => (Object.assign(Object.assign({ kind: "replace", match: byClass(cls), render }, ((opts === null || opts === void 0 ? void 0 : opts.id) ? { id: opts.id } : {})), ((opts === null || opts === void 0 ? void 0 : opts.dependsOn) ? { dependsOn: opts.dependsOn } : {})));
exports.replace = replace;
/**
 * Wraps the region's current content. Multiple wraps compose with the
 * later-registered as the outermost.
 *
 * @param cls Region class to target.
 * @param render Receives the running children and typed ctx. Returns the new node.
 * @param opts Optional id and dependsOn.
 * @returns A `WrapOperation` to include in `ClientPlugin.ui`.
 *
 * @example
 * ```ts
 * wrap("overlay-content", (children) => <Frame>{children}</Frame>)
 * ```
 */
const wrap = (cls, render, opts) => (Object.assign(Object.assign({ kind: "wrap", match: byClass(cls), render }, ((opts === null || opts === void 0 ? void 0 : opts.id) ? { id: opts.id } : {})), ((opts === null || opts === void 0 ? void 0 : opts.dependsOn) ? { dependsOn: opts.dependsOn } : {})));
exports.wrap = wrap;
/**
 * Adds content before or after the running node. Multiple augments in
 * the same slot compose in `dependsOn` then load order.
 *
 * @param cls Region class to target.
 * @param render Receives the typed ctx. Returns the node to insert.
 * @param opts Must include `slot` (`"before"` or `"after"`). May include id and dependsOn.
 * @returns An `AugmentOperation` to include in `ClientPlugin.ui`.
 *
 * @example
 * ```ts
 * augment("player-avatar", ({ playerId }) => <Badge playerId={playerId} />, { slot: "after" })
 * ```
 */
const augment = (cls, render, opts) => (Object.assign(Object.assign({ kind: "augment", match: byClass(cls), slot: opts.slot, render }, (opts.id ? { id: opts.id } : {})), (opts.dependsOn ? { dependsOn: opts.dependsOn } : {})));
exports.augment = augment;
/**
 * Removes the region entirely. No subsequent op runs in any phase.
 *
 * @param cls Region class to target.
 * @param opts Optional id and dependsOn.
 * @returns A `RemoveOperation` to include in `ClientPlugin.ui`.
 */
const remove = (cls, opts) => (Object.assign(Object.assign({ kind: "remove", match: byClass(cls) }, ((opts === null || opts === void 0 ? void 0 : opts.id) ? { id: opts.id } : {})), ((opts === null || opts === void 0 ? void 0 : opts.dependsOn) ? { dependsOn: opts.dependsOn } : {})));
exports.remove = remove;
/**
 * Escape hatch for cases the four verbs don't cover. Runs in its own
 * phase, after augment.
 *
 * @param cls Region class to target.
 * @param fn Receives the running node and typed ctx. Returns the next node (or `null` to remove).
 * @param opts Optional id and dependsOn.
 * @returns A `TransformOperation` to include in `ClientPlugin.ui`.
 *
 * @example
 * ```ts
 * transformForClass("phase-timer", (node, ctx) =>
 *   ctx.gameOver ? null : <Wrapper>{node}</Wrapper>
 * )
 * ```
 */
const transformForClass = (cls, fn, opts) => (Object.assign(Object.assign({ kind: "transform", match: byClass(cls), render: fn }, ((opts === null || opts === void 0 ? void 0 : opts.id) ? { id: opts.id } : {})), ((opts === null || opts === void 0 ? void 0 : opts.dependsOn) ? { dependsOn: opts.dependsOn } : {})));
exports.transformForClass = transformForClass;
